// Which leave year is it? — for SCREENS only (labels, default years in a form).
//
// ⚠️ DESIGN-leave-years.md. A leave year is numbered by the calendar year it STARTS in, the
// same as `LeaveAllowance.year` on the server: at an April company, 2026 = 01/04/2026 to
// 31/03/2027. The server decides which balance anything comes out of
// (backend/services/leave_year.py); nothing here may be used to pick a balance, only to
// label one or suggest a year to look at. Checked by leaveYear.checks.mjs.

export function startMonthOf(value) {
  const m = parseInt(value, 10);
  return m >= 1 && m <= 12 ? m : 1;
}

/** The leave year `today` falls in, numbered by the year it starts in. */
export function currentLeaveYear(startMonth, today = new Date()) {
  const m = startMonthOf(startMonth);
  return today.getMonth() + 1 >= m ? today.getFullYear() : today.getFullYear() - 1;
}

/** The first day of leave year `year`. */
export function leaveYearStart(startMonth, year) {
  return new Date(Number(year), startMonthOf(startMonth) - 1, 1);
}

/** The last day of leave year `year` (the day before the next one starts). */
export function leaveYearEnd(startMonth, year) {
  return new Date(Number(year) + 1, startMonthOf(startMonth) - 1, 0);
}

/**
 * The heading over a balance: "Leave year 2026/27 · ends 31/03/2027" — or, for a January
 * leave year, "Leave year 2026 · ends 31/12/2026". Rob, 29/09/2026: it replaced
 * "Year end Dec {calendar year}", which was wrong at any company whose year isn't January.
 */
export function leaveYearHeading(startMonth, year) {
  const end = leaveYearEnd(startMonth, year);
  const dd = String(end.getDate()).padStart(2, '0');
  const mm = String(end.getMonth() + 1).padStart(2, '0');
  return `Leave year ${leaveYearLabel(startMonth, year)} · ends ${dd}/${mm}/${end.getFullYear()}`;
}

/** "2026" for a January leave year, "2026/27" otherwise. */
export function leaveYearLabel(startMonth, year) {
  const y = Number(year);
  if (startMonthOf(startMonth) === 1) return String(y);
  return `${y}/${String((y + 1) % 100).padStart(2, '0')}`;
}

/**
 * The calendar years a leave year touches — [2026] for a January leave year, [2026, 2027]
 * for 2026/27. The holidays API answers per calendar year, so a screen fetches each of
 * these and keeps only `inLeaveYear` ones.
 */
export function calendarYearsOf(startMonth, year) {
  const y = Number(year);
  return startMonthOf(startMonth) === 1 ? [y] : [y, y + 1];
}

/**
 * Is 'YYYY-MM-DD' inside leave year `year`? Compared as strings, so no time zone can move
 * a bank holiday across midnight into the wrong year.
 *
 * ⚠️ 29/09/2026: the bank holidays shown beside a balance ("Plus 5 bank holiday days",
 * "Still to come") are that balance's LEAVE year's — the same ones the server deducted
 * (backend/services/bank_holidays.dates_for_user). They were the calendar year's.
 */
export function inLeaveYear(isoDate, startMonth, year) {
  const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  const day = String(isoDate).slice(0, 10);
  return day >= iso(leaveYearStart(startMonth, year)) && day <= iso(leaveYearEnd(startMonth, year));
}

/**
 * CARRY-OVER EXPIRY (DESIGN-rollover.md, 09/10/2026) — the use-by date for carried-over days
 * in leave year `year`: the last day of the `months`-th month of that leave year. 3 months at
 * a January company is 31/03; at an April company, 30/06. `null` for 0 ("never expires").
 * The SAME sum as backend/services/carry_expiry.use_by_for, which is what actually stamps the
 * date on a balance; this one only writes the hint in Settings.
 */
export function carryUseBy(startMonth, months, year) {
  const n = parseInt(months, 10);
  if (!n || n <= 0) return null;
  const last = new Date(Number(year), startMonthOf(startMonth) - 1 + n, 0);
  const end = leaveYearEnd(startMonth, year);
  return last > end ? end : last;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

/**
 * "March" — the month carried-over days must be used by the END of, which is the same every
 * year. ⚠️ A month name, not "29/02": the day changes in a leap year, and the Settings hint
 * is about every year, not one (found checking the screen, 09/10/2026). `''` for never.
 */
export function carryUseByMonth(startMonth, months) {
  const n = parseInt(months, 10);
  if (!n || n <= 0) return '';
  return MONTHS[(startMonthOf(startMonth) - 1 + Math.min(n, 12) - 1) % 12];
}

/**
 * THE YEAR PLANNERS (My leave, a person's year planner) show a whole LEAVE year — Rob 6B,
 * 30/10/2026. They used to show January to December whatever the company's year was, so at an
 * April company the planner and the balance beside it were two different years.
 *
 * `leaveYearSpan(4, 2026)` → "April 2026 to March 2027"; a January leave year keeps its old
 * words, "January to December 2026".
 */
export function leaveYearSpan(startMonth, year) {
  const m = startMonthOf(startMonth);
  const y = Number(year);
  if (m === 1) return `January to December ${y}`;
  return `${MONTHS[m - 1]} ${y} to ${MONTHS[(m + 10) % 12]} ${y + 1}`;
}

/** The twelve months of leave year `year`, first to last, as the 1st of each month. */
export function leaveYearMonths(startMonth, year) {
  const m = startMonthOf(startMonth);
  return Array.from({ length: 12 }, (_, i) => new Date(Number(year), m - 1 + i, 1));
}

/** Where a date's month sits in the leave year: 0 for its first month, 11 for its last. */
export function leaveYearMonthIndex(startMonth, date) {
  return (date.getMonth() - (startMonthOf(startMonth) - 1) + 12) % 12;
}

/** "J F M …" starting at the leave year's first month — the planners' Time off chart. */
export function leaveYearInitials(startMonth) {
  const m = startMonthOf(startMonth);
  return Array.from({ length: 12 }, (_, i) => MONTHS[(m - 1 + i) % 12][0]);
}

/** 'YYYY-MM-DD' of the leave year's first and last day — for the API's date range. */
export function leaveYearRange(startMonth, year) {
  const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  return [iso(leaveYearStart(startMonth, year)), iso(leaveYearEnd(startMonth, year))];
}
